import { forwardRef } from 'react';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { dropdownColors } from 'app/colors';
import { layers } from 'app/spacing';
import { TextEllipsisWithTooltip } from 'components/Kizen/Table';

const Label = styled.label`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  margin-bottom: unset; // Undo global label style
  // These two ensure that the height of the parent doesn't
  // change when the box is checked versus unchecked. Just an inline sizing quirk.
  line-height: 0;
  vertical-align: top;
  > input {
    position: absolute;
    opacity: 0;
    z-index: ${layers.content(0)};
    & + * {
      flex-shrink: 0;
      .RadioDot {
        display: none;
      }
    }
    &:checked + * {
      border-color: var(--color-button-secondary-hover);
      background: var(--color-background-white);
      .RadioDot {
        display: initial;
      }
    }
    &:disabled + * {
      border-color: var(--color-border-dark);
      background-color: var(--color-background-white);
      .RadioDot {
        background-color: var(--color-background-dark);
      }
    }
    &:disabled + * + * {
      color: var(--color-background-dark);
    }
    &:focus-visible + * {
      border-color: var(--color-button-secondary-hover);
      background: var(--color-background-white);
    }
    &:focus-visible:checked + * {
      background-color: ${dropdownColors.hover};
    }
  }
  &:hover > input:not(:disabled) + * {
    border-color: var(--color-button-secondary-hover);
    background: var(--color-background-white);
  }
  &:hover > input:checked:not(:disabled) + *,
  &:focus-visible > input:checked:not(:disabled) + * {
    background-color: ${dropdownColors.hover};
  }
  ${({ error }) =>
    error &&
    css`
      > input {
        & + *,
        &:checked + * {
          border-color: var(--color-button-warning-hover);
        }
      }
      &:hover > input:not(:disabled) + * {
        border-color: var(--color-button-warning-hover);
      }
    `}
`;

const VisualRadio = styled.span`
  box-sizing: content-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-border-dark);
  background-color: var(--color-background-white);
  border-radius: 50%;
  &:not(:last-child) {
    margin-right: 10px;
  }
  width: 15px;
  height: 15px;
  .RadioDot {
    background-color: var(--color-button-secondary-hover);
    border-radius: 50%;
    height: 11px;
    width: 11px;
  }
  ${({ variant }) =>
    variant === 'underline' &&
    css`
      width: 12px;
      height: 12px;
      .RadioDot {
        height: 10px;
        width: 10px;
      }
    `}
`;

export const Radio = forwardRef(
  ({ label, variant, error, onChange, className, ...props }, ref) => {
    return (
      <Label className={className} error={error}>
        <input
          ref={ref}
          type="radio"
          aria-checked={props.checked}
          onChange={(ev) => {
            if (onChange) onChange(ev.target.checked, ev);
          }}
          {...props}
        />
        <VisualRadio variant={variant}>
          <div className="RadioDot" />
        </VisualRadio>
        {typeof label !== 'string' && label}
        {typeof label === 'string' && (
          <TextEllipsisWithTooltip as="span">{label}</TextEllipsisWithTooltip>
        )}
      </Label>
    );
  }
);

export default Radio;
